import { DoorsFormState } from "company/detail/components/models";
import { ShowDates } from "components/showdatepicker/models";
import moment from "moment";
import { parseFromTime } from "utilities";

export interface ShowInfo {
  production: server.dto.ProductionSmallV2
  showDates: ShowDates
  status: server.dto.StatusSmall
  eventName: string
  eventWebsite: string
  publication: server.dto.PublicationStatuses
  costCenter: server.dto.CostCenter
  projectCode: string
  tags: server.dto.TagSmall[]
  owner: server.dto.UserSmall
  artist: server.dto.ArtistSmall
  attendees: server.dto.ContactSmallV2[]
  venue: server.dto.VenueSmall
  capacity: number
  ageRestriction: server.dto.AgeRestrictions
  doors: DoorsFormState
  venueContact: server.dto.ContactSmallV2
  promoter: server.dto.CompanySmall
  promoterContact: server.dto.ContactSmallV2
  hotel: server.dto.CompanySmall
  hotelContact: server.dto.ContactSmallV2
  onSaleDate: moment.Moment | null
  soldOut: boolean
  ticketLink: string
  presalePrice: number | null
  doorPrice: number | null
  reducedPrice: number | null
  bookkeeping: server.dto.BookkeepingSmall
  currency: server.dto.Currencies
  exchangeRate: number
  freefieldValues: server.dto.FreeFieldValue[]
};

export interface ExternalShowInfo {
  production: server.dto.ProductionSmallV2
  showDates: ShowDates
  status: server.dto.StatusSmall
  eventName: string
  eventWebsite: string
  artist: server.dto.ArtistSmall
  freefieldValues: server.dto.FreeFieldValue[]
}

const getTicketPrice = (show: server.dto.Show, type: server.dto.TicketTypes) => show.tickets?.tickets?.find(t => t.type.value === type)?.price ?? null;

export const mapShowToShowInfo = (show: server.dto.Show): ShowInfo => ({
  production: show.production,
  showDates: {
    startDisplayDate: moment.utc(show.dateTime.startDisplay),
    endDisplayDate: moment.utc(show.dateTime.endDisplay),
    mode: show.dateTime.mode,
    timeOfPerformance: show.dateTime.customFallbackTime
  },
  status: show.status,
  eventName: show.eventName,
  eventWebsite: show.eventWebsite ?? "",
  publication: show.publication.value,
  costCenter: show.costCenter,
  projectCode: show.projectCode,
  tags: show.tags,
  owner: show.owner,
  artist: show.artist,
  attendees: show.attendees,
  venue: show.venue,
  venueContact: show.venueContact,
  capacity: show.capacity,
  ageRestriction: show.ageRestriction,
  doors: {
    open: show.doors?.open ? parseFromTime(show.doors.open) : null,
    close: show.doors?.close ? parseFromTime(show.doors.close) : null,
  },
  promoter: show.promoter,
  promoterContact: show.promoterContact,
  hotel: show.hotel,
  hotelContact: show.hotelContact,
  onSaleDate: show.tickets?.onSaleDate ? moment.utc(show.tickets.onSaleDate) : null,
  soldOut: show.tickets?.soldOut ?? false,
  ticketLink: show.tickets?.ticketLink?.value ?? "",

  presalePrice: getTicketPrice(show, "presale"),
  doorPrice: getTicketPrice(show, "door"),
  reducedPrice: getTicketPrice(show, "reduced"),

  bookkeeping: show.bookkeeping,
  currency: show.currency,
  exchangeRate: show.exchangeRate,

  freefieldValues: show.freeFields
})

export const mapShowInfoToInsertShowInternalInstruction = (
  showInfo: ShowInfo,
  production: server.dto.ProductionSmallV2
): server.dto.InsertShowInternalEnvironmentInstruction => ({
  dateTime: {
    startDisplay: showInfo.showDates.startDisplayDate.toDate(),
    endDisplay: showInfo.showDates.endDisplayDate.toDate(),
    customFallbackTime: showInfo.showDates.timeOfPerformance,
    mode: showInfo.showDates.mode
  },
  statusId: showInfo.status.statusId,
  eventName: showInfo.eventName,
  eventWebsite: showInfo.eventWebsite,
  publication: showInfo.publication,
  costCenterId: showInfo.costCenter?.costCenterId,
  projectCode: showInfo.projectCode,
  ownerUserId: showInfo.owner?.userId,
  artistId: showInfo.artist?.artistId,
  attendees: showInfo.attendees.map(a => a.contactId),
  venueCompanyId: showInfo.venue?.venueId,
  venueContactId: showInfo.venueContact?.contactId,
  capacity: showInfo.capacity,
  ageRestriction: showInfo.ageRestriction,
  doorsOpen: showInfo.doors.open?.format("HH:mm"),
  doorsClose: showInfo.doors.close?.format("HH:mm"),
  promoterCompanyId: showInfo.promoter?.companyId,
  promoterContactId: showInfo.promoterContact?.contactId,
  hotelCompanyId: showInfo.hotel?.companyId,
  hotelContactId: showInfo.hotelContact?.contactId,
  tickets: {
    onSaleDate: showInfo.onSaleDate?.toDate() ?? null,
    soldOut: showInfo.soldOut,
    doorPrice: showInfo.doorPrice,
    presalePrice: showInfo.presalePrice,
    reducedPrice: showInfo.reducedPrice,
    ticketSalesWebsite: showInfo.ticketLink
  },
  bookkeepingId: showInfo.bookkeeping.bookkeepingId,
  exchangeRate: showInfo.exchangeRate,
  currency: showInfo.currency,
  productionId: production?.productionId,
  freeFields: showInfo.freefieldValues,
  tagIds: showInfo.tags.map(t => t.tagId),
  validationStatus: "validated"
})

export const mapExternalShowInfoToInsertShowExternalInstruction = (
  showInfo: ExternalShowInfo
): server.dto.InsertShowExternalEnvironmentInstruction => ({
  dateTime: {
    startDisplay: showInfo.showDates.startDisplayDate.toDate(),
    endDisplay: showInfo.showDates.endDisplayDate.toDate(),
    customFallbackTime: showInfo.showDates.timeOfPerformance,
    mode: showInfo.showDates.mode
  },
  statusId: showInfo.status.statusId,
  eventName: showInfo.eventName,
  eventWebsite: showInfo.eventWebsite,
  freeFields: showInfo.freefieldValues,
  artistId: showInfo.artist.artistId,
  productionId: showInfo.production?.productionId
})

// TODO: ShowID should be included here, we should implement it when we do the Show Edit page
//  Also, it makes sense to include the freefieldState, bookkeepingCurrency, and production in the ShowInfo
export const mapShowInfoToUpdateShowInternalInstruction = (
  showInfo: ShowInfo,
  production: server.dto.ProductionSmallV2
): Omit<server.dto.UpdateShowInternalEnvironmentInstruction, "showId"> => ({
  dateTime: {
    startDisplay: showInfo.showDates.startDisplayDate.toDate(),
    endDisplay: showInfo.showDates.endDisplayDate.toDate(),
    customFallbackTime: showInfo.showDates.timeOfPerformance,
    mode: showInfo.showDates.mode
  },
  statusId: showInfo.status.statusId,
  eventName: showInfo.eventName,
  eventWebsite: showInfo.eventWebsite,
  publication: showInfo.publication,
  costCenterId: showInfo.costCenter?.costCenterId,
  projectCode: showInfo.projectCode,
  ownerUserId: showInfo.owner?.userId,
  artistId: showInfo.artist?.artistId,
  attendees: showInfo.attendees.map(a => a.contactId),
  venueCompanyId: showInfo.venue?.venueId,
  venueContactId: showInfo.venueContact?.contactId,
  capacity: showInfo.capacity,
  ageRestriction: showInfo.ageRestriction,
  doors: {
    open: showInfo.doors.open?.format("HH:mm") ?? null,
    close: showInfo.doors.close?.format("HH:mm") ?? null,
  },
  promoterCompanyId: showInfo.promoter?.companyId,
  promoterContactId: showInfo.promoterContact?.contactId,
  hotelCompanyId: showInfo.hotel?.companyId,
  hotelContactId: showInfo.hotelContact?.contactId,
  tickets: {
    onSaleDate: showInfo.onSaleDate?.toDate() ?? null,
    soldOut: showInfo.soldOut,
    doorPrice: showInfo.doorPrice,
    presalePrice: showInfo.presalePrice,
    reducedPrice: showInfo.reducedPrice,
    ticketSalesWebsite: showInfo.ticketLink
  },
  bookkeepingId: showInfo.bookkeeping.bookkeepingId,
  exchangeRate: showInfo.exchangeRate,
  currency: showInfo.currency,
  productionId: production?.productionId
})